/**
 * Asset stage for the media studio dialog. Renders the asset clean — no
 * floating overlays. AI-Temp shimmer (skill-purple wash + dashed border +
 * spinner) appears only while an operation is in flight; the tint sits ON
 * TOP of the asset so the underlying media stays visible.
 *
 * When `cropMode` is true (image only), replaces the static <img> with the
 * MediaStudioCropOverlay (ReactCrop + aspect dropdown + Save / Cancel toolbar).
 *
 * Note: PD-5.4 removed the `floatingBar` slot — composer moved to the right
 * rail (per user UAT screenshot 2 + Steve Jobs critique that overlays
 * crowd the asset).
 */
import { ImageBrokenIcon } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';

import { MediaStudioCropOverlay } from './MediaStudioCropOverlay';
import { MediaStudioVideoPlayer } from './MediaStudioVideoPlayer';
import { MediaStudioTextOverlay } from './text-editor/MediaStudioTextOverlay';
import { TextEditorErrorBoundary } from './text-editor/TextEditorErrorBoundary';
import type { LogoElement, ShapeElement, TextElement } from './text-editor/useTextEditorStore';
import { type MediaStudioPayload } from './types';

import { Spinner } from '@/components/ui/spinner';
import { useBrandKit } from '@/hooks/useBrandKit';
import type { OverlayLayer } from '@/lib/media-studio/overlay-types';
import { ARTIFACT_PREVIEW_OBJECT, ARTIFACT_PREVIEW_STAGE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { getLqipUrl } from '@/utils/image-optimization';
import { logger } from '@/utils/logger';

interface MediaStudioAssetStageProps {
  payload: MediaStudioPayload;
  isOperating: boolean;
  /** When true (image-only), replaces the static image with a crop overlay. */
  cropMode?: boolean;
  onCropCancel?: () => void;
  onCropSave?: (blob: Blob) => Promise<void> | void;
  cropSaving?: boolean;
  textOverlayMode?: boolean;
  onTextOverlayCancel?: () => void;
  onTextOverlaySave?: (blob: Blob) => Promise<void> | void;
  onTextOverlayLayersSave?: (
    layers: (TextElement | LogoElement | ShapeElement)[],
    stageWidth: number,
  ) => Promise<void> | void;
  textOverlaySaving?: boolean;
  /** Persisted overlay layers for re-editing in text mode. */
  overlayLayers?: OverlayLayer[];
  /** Overlay layers pre-converted to TextElement/LogoElement format for the text editor. */
  overlayLayersAsTextElements?: (TextElement | LogoElement | ShapeElement)[];
}

export function MediaStudioAssetStage({
  payload,
  isOperating,
  cropMode = false,
  onCropCancel,
  onCropSave,
  cropSaving = false,
  textOverlayMode = false,
  onTextOverlayCancel,
  onTextOverlaySave,
  onTextOverlayLayersSave,
  textOverlaySaving = false,
  overlayLayers,
  overlayLayersAsTextElements,
}: MediaStudioAssetStageProps) {
  const isImageCropMode = cropMode && payload.kind === 'image';
  const isTextOverlayMode = textOverlayMode && payload.kind === 'image';
  const { brandKit } = useBrandKit();
  const defaultFontFamily = brandKit?.font_primary || 'Inter';

  // When the asset URL fails to load (404, expired signature, deleted from
  // storage), show a graceful empty state instead of the browser's native
  // broken-image glyph. Reset on payload.url change so a new asset can recover.
  const [loadFailed, setLoadFailed] = useState(false);
  // Tracks when the real asset has actually painted pixels — drives the
  // skeleton below. Without this, an asset with no LQIP thumbnail rendered
  // nothing while its full-res file was in flight (blank canvas), and assets
  // WITH an LQIP still needed a signal for when to retire the blur-up so it
  // doesn't linger behind a letterboxed (object-contain) image forever.
  const [assetReady, setAssetReady] = useState(false);
  const urlRef = useRef(payload.url);
  if (urlRef.current !== payload.url) {
    urlRef.current = payload.url;
    if (loadFailed) setLoadFailed(false);
    if (assetReady) setAssetReady(false);
  }
  const [flattenedPreview, setFlattenedPreview] = useState<{
    source: string;
    url: string;
  } | null>(null);
  const hasOverlayLayers = payload.kind === 'image' && (overlayLayers?.length ?? 0) > 0;
  useEffect(() => {
    if (!hasOverlayLayers || !overlayLayers) {
      setFlattenedPreview(null);
      return;
    }
    let cancelled = false;
    void import('@/lib/media-studio/request-composite')
      .then(({ requestCompositedUrl }) => requestCompositedUrl(payload.url, overlayLayers))
      .then(({ url, composited }) => {
        if (!cancelled) setFlattenedPreview(composited ? { source: payload.url, url } : null);
      })
      .catch((err) => {
        logger.error('Could not render saved text preview', err);
        if (!cancelled) setFlattenedPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [hasOverlayLayers, overlayLayers, payload.url]);
  const displayUrl =
    flattenedPreview && flattenedPreview.source === payload.url
      ? flattenedPreview.url
      : payload.url;
  const showFallback = loadFailed && !isImageCropMode && !isTextOverlayMode;
  const showSkeleton = !assetReady && !showFallback && !isImageCropMode && !isTextOverlayMode;

  return (
    <div
      className={cn(
        // `bg-canvas` (ARTIFACT_PREVIEW_STAGE), not flat `bg-background` —
        // reversed 2026-09-06. The header/toolbar above this stage stays
        // `bg-background`; the stage itself gets the quiet, subtly-lifted
        // "media on a table" tone PostBody's preview stage already uses
        // (ui-tokens.ts:1589), so the asset visibly sits on its own surface
        // instead of reading as flush with the chrome — the ChatGPT
        // image-viewer model this was previously (incorrectly) claimed to
        // already match. Crop/text mode owns a dedicated editing canvas
        // inside EditModeShell.
        isImageCropMode || isTextOverlayMode
          ? 'relative flex min-h-[40vh] w-full flex-1 flex-col overflow-hidden md:min-h-0'
          : 'relative flex min-h-[40vh] w-full flex-1 items-end justify-center overflow-hidden p-2 md:min-h-0 md:items-center md:p-4',
        ARTIFACT_PREVIEW_STAGE,
      )}
    >
      {showSkeleton ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-2 animate-pulse rounded-md bg-muted/40 md:inset-4"
        />
      ) : null}
      {showFallback ? (
        <div className="relative self-center flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
          <div className="flex size-16 items-center justify-center rounded-full bg-muted/40">
            <ImageBrokenIcon className="size-8 text-muted-foreground/50" weight="regular" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground/85">This file is unavailable</p>
            <p className="max-w-xs text-detail text-muted-foreground">
              The {payload.kind === 'image' ? 'image' : 'video'} may have moved or been deleted from
              storage. Try regenerating or uploading a replacement.
            </p>
          </div>
        </div>
      ) : isImageCropMode ? (
        <MediaStudioCropOverlay
          imageUrl={payload.url}
          onCancel={onCropCancel ?? (() => {})}
          onSave={onCropSave ?? (() => {})}
          isSaving={cropSaving}
        />
      ) : isTextOverlayMode ? (
        <TextEditorErrorBoundary onReset={onTextOverlayCancel ?? (() => {})}>
          <MediaStudioTextOverlay
            imageUrl={payload.url}
            defaultFontFamily={defaultFontFamily}
            brandKit={brandKit ?? null}
            onCancel={onTextOverlayCancel ?? (() => {})}
            onSave={onTextOverlaySave ?? (() => {})}
            onLayersSave={onTextOverlayLayersSave}
            isSaving={textOverlaySaving}
            initialLayers={overlayLayersAsTextElements}
          />
        </TextEditorErrorBoundary>
      ) : payload.kind === 'image' ? (
        // Use raw payload.url + crossOrigin="anonymous" so this matches the
        // cache key used by MediaStudioCropOverlay and MediaStudioTextOverlay.
        // Previously this used getOptimizedImageUrl() (different URL + no CORS),
        // which caused a full re-fetch when entering edit modes — the visible
        // "image disappears and reloads" flash. Single cache entry now.
        <img
          src={displayUrl}
          alt={payload.title}
          loading="eager"
          decoding="async"
          crossOrigin="anonymous"
          onLoad={() => setAssetReady(true)}
          onError={() => setLoadFailed(true)}
          className={cn('relative max-h-full max-w-full object-contain', ARTIFACT_PREVIEW_OBJECT)}
          style={
            getLqipUrl(payload.url)
              ? {
                  backgroundImage: `url("${getLqipUrl(payload.url)}")`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }
              : undefined
          }
        />
      ) : (
        <MediaStudioVideoPlayer
          src={payload.url}
          poster={payload.posterUrl}
          ariaLabel={payload.title}
          onLoadedData={() => setAssetReady(true)}
          onError={() => setLoadFailed(true)}
          className={cn('relative max-h-full max-w-full object-contain', ARTIFACT_PREVIEW_OBJECT)}
        />
      )}
      <div
        aria-hidden={!isOperating}
        className={[
          'pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2',
          'bg-skill/[0.06] border border-dashed border-skill/30 rounded-md',
          'transition-opacity duration-200',
          isOperating ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      >
        <Spinner className="text-skill" />
        <span className="text-detail font-medium text-skill/80">Composing…</span>
      </div>
    </div>
  );
}
